import React, { createContext, useContext, useReducer, useEffect } from 'react';
import apiService from '../services/api';

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CART':
      return {
        ...state,
        items: action.payload,
      };
    
    case 'ADD_TO_CART':
      const existingItem = state.items.find(item => 
        item.id === action.payload.id || 
        item.variant_id === action.payload.variant_id
      );
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            (item.id === action.payload.id || item.variant_id === action.payload.variant_id)
              ? { ...item, quantity: item.quantity + (action.payload.quantity || 1) }
              : item
          )
        };
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, quantity: action.payload.quantity || 1 }]
      };
    
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => 
          item.id !== action.payload && item.variant_id !== action.payload
        )
      };
    
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          (item.id === action.payload.id || item.variant_id === action.payload.id)
            ? { ...item, quantity: Math.max(0, action.payload.quantity) }
            : item
        ).filter(item => item.quantity > 0)
      };
    
    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };
    
    default:
      return state;
  }
};

const initialState = {
  items: [],
  isOpen: false
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState, () => {
    // Load cart from localStorage for guest users
    const savedCart = localStorage.getItem('bakelore-cart');
    return savedCart ? JSON.parse(savedCart) : initialState;
  });

  // Save to localStorage whenever cart changes (for guest users)
  useEffect(() => {
    localStorage.setItem('bakelore-cart', JSON.stringify(state));
  }, [state]);

  // Sync with backend cart if user is authenticated
  useEffect(() => {
    const syncCart = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const cartData = await apiService.cart.get();
          if (cartData && cartData.items) {
            dispatch({ type: 'SET_CART', payload: cartData.items });
          }
        } catch (error) {
          console.error('Error syncing cart:', error);
        }
      }
    };
    
    syncCart();
  }, []);

  const addToCart = async (product, variantId = null, quantity = 1, customText = null) => {
    const token = localStorage.getItem('token');
    
    try {
      // For authenticated users, sync with backend
      if (token && variantId) {
        try {
          await apiService.cart.addItem({
            product_variant_id: variantId,
            quantity,
            custom_text: customText,
          });
        } catch (apiError) {
          console.error('Backend cart sync failed:', apiError);
          // Continue with local update even if API fails
        }
      }
      
      // Update local state
      dispatch({ 
        type: 'ADD_TO_CART', 
        payload: { 
          ...product, 
          id: product.id,
          variant_id: variantId,
          quantity,
          custom_text: customText,
          price: product.price || product.variants?.find(v => v.id === variantId)?.price || 0
        } 
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error adding to cart:', error);
      return { success: false, error: error.message };
    }
  };

  const removeFromCart = async (productId) => {
    const token = localStorage.getItem('token');
    
    try {
      // For authenticated users, sync with backend
      if (token) {
        const item = state.items.find(i => i.id === productId || i.variant_id === productId);
        if (item && item.cart_item_id) {
          try {
            await apiService.cart.removeItem(item.cart_item_id);
          } catch (apiError) {
            console.error('Backend cart remove failed:', apiError);
          }
        }
      }
      
      // Update local state
      dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
      return { success: true };
    } catch (error) {
      console.error('Error removing from cart:', error);
      return { success: false, error: error.message };
    }
  };

  const updateQuantity = async (productId, quantity) => {
    const token = localStorage.getItem('token');
    
    // Validate quantity
    if (quantity < 0) {
      console.error('Invalid quantity:', quantity);
      return { success: false, error: 'Invalid quantity' };
    }
    
    try {
      // For authenticated users, sync with backend
      if (token) {
        const item = state.items.find(i => i.id === productId || i.variant_id === productId);
        if (item && item.cart_item_id) {
          try {
            await apiService.cart.updateItem(item.cart_item_id, quantity);
          } catch (apiError) {
            console.error('Backend cart update failed:', apiError);
          }
        }
      }
      
      // Update local state
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
      return { success: true };
    } catch (error) {
      console.error('Error updating quantity:', error);
      return { success: false, error: error.message };
    }
  };

  const clearCart = async () => {
    const token = localStorage.getItem('token');
    
    try {
      // For authenticated users, sync with backend
      if (token) {
        try {
          await apiService.cart.clear();
        } catch (apiError) {
          console.error('Backend cart clear failed:', apiError);
        }
      }
      
      // Update local state
      dispatch({ type: 'CLEAR_CART' });
      return { success: true };
    } catch (error) {
      console.error('Error clearing cart:', error);
      return { success: false, error: error.message };
    }
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => {
      let price = 0;
      if (typeof item.price === 'string') {
        price = parseFloat(item.price.replace(/[^0-9.]/g, ''));
      } else if (typeof item.price === 'number') {
        price = item.price;
      }
      return total + (price * item.quantity);
    }, 0);
  };

  const getCartItemsCount = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      ...state,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartItemsCount
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};